import { defineStore, acceptHMRUpdate } from "pinia"
import type { Create, ListsRecord, Update } from "@/pocketbase-types"
import { sortBy } from "@/utils"
import { useListItemStore } from "./list-item"
import { useHistoryStore } from "./history"
import { useReminderStore } from "./reminder"

export const useListStore = defineStore("list", {
  state: () => ({
    current: null as ListsRecord | null,
    lists: [] as ListsRecord[]
  }),
  getters: {},
  actions: {
    async setCurrent(id?: string | null) {
      id ||= localStorage.getItem("currentList")

      this.current =
        this.lists.find(l => l.id === id) ?? this.lists.find(l => l.name === "Default") ?? this.lists[0] ?? null

      if (this.current) {
        localStorage.setItem("currentList", this.current.id)
      } else {
        localStorage.removeItem("currentList")
      }
    },
    async refresh() {
      this.lists = await this.pb.collection("lists").getFullList({ sort: "+name" })
      await this.setCurrent()
    },
    async create(props: Omit<Create<"lists">, "household">) {
      const list = await this.pb
        .collection("lists")
        .create({ ...props, household: this.pb.authStore.record?.household })
      this.lists.push(list)
      this.lists.sort(sortBy(l => l.name))
    },
    async update(list: ListsRecord, props: Update<"lists">) {
      const updated = await this.pb.collection("lists").update(list.id, props)
      const idx = this.lists.findIndex(l => l.id == updated.id)

      if (idx > -1) {
        this.lists.splice(idx, 1, updated)
        this.lists.sort(sortBy(l => l.name))
      } else {
        await this.refresh()
      }
    },
    async delete(list: ListsRecord | null) {
      if (!list) return

      await this.pb.collection("lists").delete(list.id)
      const idx = this.lists.findIndex(l => l.id == list.id)
      if (idx > -1) {
        this.lists.splice(idx, 1)
      }
      await this.setCurrent()
    },
    async checkout(props: { dateAt: string; account: string }) {
      const listItemStore = useListItemStore()
      const historyStore = useHistoryStore()
      const reminderStore = useReminderStore()

      await this.pb.send("/api/checkout", { method: "POST", body: { ...props, list: this.current?.id } })
      await listItemStore.refresh()
      await historyStore.refresh()
      await reminderStore.refresh()
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useListStore, import.meta.hot))
}
